import { useEffect, useRef, useState } from "react";
import { Heart, Coins, ArrowDownToLine, Flame, Star, Sword, Shield } from "lucide-react";

/** Floating +/- number animation over the health value. */
function HpFloat({ value }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (value === 0 || value === null || value === undefined) return;
    setVisible(true);
    const t = setTimeout(() => setVisible(false), 1600);
    return () => clearTimeout(t);
  }, [value]);
  if (!visible || !value) return null;
  const positive = value > 0;
  return (
    <span
      key={value + ":" + Date.now()}
      data-testid="hp-float"
      className="absolute -top-4 right-0 font-heading text-2xl pointer-events-none"
      style={{
        color: positive ? "#7cffa0" : "#ff6b3a",
        textShadow: positive ? "0 0 12px rgba(124,255,160,0.7)" : "0 0 12px rgba(255,107,58,0.7)",
        animation: "hpFloat 1.6s ease-out forwards",
      }}
    >
      {positive ? "+" : ""}{value}
    </span>
  );
}

export default function StatsHUD({
  health, maxHealth, gold, floor,
  livesRemaining, dailyMax, bonusLives,
  level = 1, xp = 0, xpNeeded = 50, maxLevel = 100,
  hpDelta = 0, goldDelta = 0, xpDelta = 0,
  attack = 0, defense = 0,
  critChance = 0, lifesteal = 0, dmgMult = 1.0, weaponTier = "common",
  enemyName,
}) {
  const hpPct = Math.max(0, Math.min(100, (health / maxHealth) * 100));
  const levelSafe = Math.min(maxLevel, Math.max(1, level));
  const xpInto = xp - (levelSafe - 1) * xpNeeded;
  const xpPct = levelSafe >= maxLevel ? 100 : Math.max(0, Math.min(100, (xpInto / xpNeeded) * 100));

  // Small state trick so HpFloat re-fires on every action even if hpDelta equals last one
  const [hpTick, setHpTick] = useState(0);
  const prevRef = useRef(0);
  useEffect(() => {
    if (hpDelta !== 0 && hpDelta !== prevRef.current) {
      setHpTick((n) => n + 1);
      prevRef.current = hpDelta;
    }
  }, [hpDelta]);

  return (
    <div className="panel p-5" data-testid="stats-hud">
      {/* Enemy nameplate */}
      {enemyName ? (
        <div className="mb-3 -mt-1 flex items-center gap-2 text-[10px] tracking-[0.3em] blood-text" data-testid="enemy-name">
          <span className="w-1.5 h-1.5 bg-[#8a0303] rounded-full animate-pulse" />
          FIGHTING · <span className="text-[#f0e6d2]">{enemyName.toUpperCase()}</span>
        </div>
      ) : null}

      {/* Level + XP */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-[10px] tracking-[0.3em] ash-text">
          <span className="flex items-center gap-1"><Star size={10} className="gold-text" /> LEVEL</span>
          <span data-testid="stat-xp">XP {xp}{levelSafe < maxLevel ? ` / ${levelSafe * xpNeeded}` : " · MAX"}</span>
        </div>
        <div className="flex items-baseline gap-2">
          <div className="font-heading text-3xl gold-text" data-testid="stat-level">{levelSafe}</div>
          <div className="text-xs ash-text">/ {maxLevel}</div>
        </div>
        <div className="mt-1 h-[6px] bg-black/60 border border-[#d4af37]/25 overflow-hidden">
          <div className="h-full transition-all duration-700" style={{ width: `${xpPct}%`, background: "linear-gradient(90deg, #d4af37, #f0e6d2)" }} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="relative">
          <div className="text-[10px] tracking-[0.3em] ash-text flex items-center gap-1">
            <Heart size={10} className="blood-text" /> HEALTH
          </div>
          <div className="mt-1 text-2xl font-heading blood-text" data-testid="stat-health">
            {health}<span className="text-sm ash-text">/{maxHealth}</span>
          </div>
          <div className="hp-bar mt-2"><span style={{ width: `${hpPct}%` }} /></div>
          <span key={hpTick}><HpFloat value={hpDelta} /></span>
        </div>
        <div>
          <div className="text-[10px] tracking-[0.3em] ash-text flex items-center gap-1">
            <Coins size={10} className="gold-text" /> GOLD
          </div>
          <div className="mt-1 text-2xl font-heading gold-text" data-testid="stat-gold">
            {gold}
            {goldDelta > 0 && <span className="ml-2 text-xs" style={{ color: "#7cffa0" }} data-testid="gold-delta">{" "}+{goldDelta}</span>}
          </div>
        </div>
        <div>
          <div className="text-[10px] tracking-[0.3em] ash-text flex items-center gap-1">
            <ArrowDownToLine size={10} className="arcane-text" /> FLOOR
          </div>
          <div className="mt-1 text-2xl font-heading arcane-text" data-testid="stat-floor">{floor}</div>
        </div>
        <div>
          <div className="text-[10px] tracking-[0.3em] ash-text flex items-center gap-1">
            <Flame size={10} className="ember" /> LIVES
          </div>
          <div className="mt-1 text-2xl font-heading text-[#e25822]" data-testid="stat-lives">
            {livesRemaining}<span className="text-sm ash-text">/{dailyMax}</span>
            {bonusLives > 0 && <span className="text-sm gold-text ml-1">+{bonusLives}</span>}
          </div>
        </div>
      </div>

      {/* Attack / Defense */}
      <div className="mt-4 pt-3 border-t border-white/10 grid grid-cols-2 gap-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] ash-text flex items-center gap-1">
            <Sword size={10} className="text-[#e25822]" /> ATTACK
          </div>
          <div className="mt-1 font-heading text-xl text-[#e25822]" data-testid="stat-attack">+{attack}</div>
        </div>
        <div>
          <div className="text-[10px] tracking-[0.3em] ash-text flex items-center gap-1">
            <Shield size={10} className="arcane-text" /> DEFENSE
          </div>
          <div className="mt-1 font-heading text-xl arcane-text" data-testid="stat-defense">+{defense}</div>
        </div>
      </div>

      {/* Weapon tier + advanced combat modifiers */}
      {(critChance > 0 || lifesteal > 0 || dmgMult > 1.0 || weaponTier !== "common") && (
        <div className="mt-3 pt-3 border-t border-white/10" data-testid="stat-weapon-mods">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] tracking-[0.3em] ash-text">WEAPON TIER</span>
            <TierChip tier={weaponTier} />
          </div>
          <div className="flex flex-wrap gap-2">
            {dmgMult > 1.0 && (
              <ModChip
                testid="mod-dmgmult"
                label="DMG"
                value={`×${dmgMult.toFixed(2)}`}
                color="#e25822"
              />
            )}
            {critChance > 0 && (
              <ModChip
                testid="mod-crit"
                label="CRIT"
                value={`${Math.round(critChance * 100)}%`}
                color="#ffb56b"
              />
            )}
            {lifesteal > 0 && (
              <ModChip
                testid="mod-lifesteal"
                label="LIFESTEAL"
                value={`${Math.round(lifesteal * 100)}%`}
                color="#ff5f8a"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const TIER_STYLES = {
  common:    { color: "#a89373", label: "COMMON",    shadow: "none" },
  rare:      { color: "#5db7ff", label: "RARE",      shadow: "0 0 10px rgba(93,183,255,0.4)" },
  epic:      { color: "#c084fc", label: "EPIC",      shadow: "0 0 12px rgba(192,132,252,0.5)" },
  legendary: { color: "#ffb56b", label: "LEGENDARY", shadow: "0 0 14px rgba(255,181,107,0.6)" },
};

function TierChip({ tier }) {
  const t = TIER_STYLES[tier] || TIER_STYLES.common;
  return (
    <span
      data-testid={`weapon-tier-${tier}`}
      className="inline-flex items-center px-2 py-0.5 text-[10px] tracking-[0.35em] font-heading"
      style={{
        color: t.color,
        border: `1px solid ${t.color}`,
        boxShadow: t.shadow,
        background: "rgba(0,0,0,0.4)",
      }}
    >
      {t.label}
    </span>
  );
}

function ModChip({ label, value, color, testid }) {
  return (
    <div
      data-testid={testid}
      className="inline-flex items-center gap-1.5 px-2 py-1 text-[10px] tracking-[0.25em]"
      style={{
        color,
        border: `1px solid ${color}55`,
        background: "rgba(0,0,0,0.35)",
      }}
    >
      <span className="opacity-80">{label}</span>
      <span className="font-heading" style={{ color: "#f0e6d2" }}>{value}</span>
    </div>
  );
}
